import { useEffect, type MutableRefObject } from "react";
import type { SessionStatus } from "@cabal/shared/domain/session/enums.js";
import { LogLevel, useSeqLog } from "@/hooks/telemetry";
import {
  PollingSetupSource,
  getPollingIntervals,
  type PollingIntervals,
} from "./session-data-provider-utils";

type SessionProviderLogger = ReturnType<typeof useSeqLog>;

interface UseSessionPollingRestartInput {
  sessionStatusValue: SessionStatus | null | undefined;
  sessionGenerationRef: MutableRefObject<number>;
  haltStatusDebugLog: SessionProviderLogger;
  isPollingRunning: () => boolean;
  clearPolling: () => void;
  startPolling: (input: {
    intervals: PollingIntervals;
    source: PollingSetupSource;
  }) => void;
}

interface RestartPollingIfRunningInput extends UseSessionPollingRestartInput {
  normalizedSessionStatus: SessionStatus | null;
}

export function useSessionPollingRestart({
  sessionStatusValue,
  sessionGenerationRef,
  haltStatusDebugLog,
  isPollingRunning,
  clearPolling,
  startPolling,
}: UseSessionPollingRestartInput): void {
  useEffect(() => {
    restartPollingIfRunning({
      sessionStatusValue,
      normalizedSessionStatus: sessionStatusValue ?? null,
      sessionGenerationRef,
      haltStatusDebugLog,
      isPollingRunning,
      clearPolling,
      startPolling,
    });
  }, [
    sessionStatusValue,
    sessionGenerationRef,
    haltStatusDebugLog,
    startPolling,
    clearPolling,
    isPollingRunning,
  ]);
}

function restartPollingIfRunning({
  normalizedSessionStatus,
  sessionGenerationRef,
  haltStatusDebugLog,
  isPollingRunning,
  clearPolling,
  startPolling,
}: RestartPollingIfRunningInput): void {
  if (!isPollingRunning()) {
    haltStatusDebugLog(
      "restart-effect: SKIP (polls not running)",
      { sessionStatusValue: normalizedSessionStatus },
      LogLevel.Warning,
    );
    return;
  }

  const intervals = getPollingIntervals(normalizedSessionStatus);
  haltStatusDebugLog(
    "restart-effect: FIRED (status changed → re-tier intervals)",
    {
      sessionStatusValue: normalizedSessionStatus,
      newSessionIntervalMs: intervals.session,
      generation: sessionGenerationRef.current,
    },
    LogLevel.Info,
  );

  clearPolling();
  startPolling({
    intervals,
    source: PollingSetupSource.RestartEffect,
  });
}
